import * as React from "react";
import { CircleDashed } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";

/**
 * Phase F-finale-5 Slice 5a — banner rendered on the IA forms pages when
 * the SA has paused forms editing for the tenant
 * (`feature_flags.forms_editable_by_tenant === false`).
 *
 * Mirrors `TenantAuthoringPausedBanner` (H5d): informational amber tone,
 * not destructive. Reads stay available (forms still render in admissions
 * / enrolment) — only the authoring write surface is gated.
 */
export interface TenantFormsPausedBannerProps {
  className?: string;
  body?: React.ReactNode;
}

export function TenantFormsPausedBanner({ className, body }: TenantFormsPausedBannerProps) {
  return (
    <div
      role="status"
      data-testid="forms-editing-paused-banner"
      className={cn(
        "border-amber-500/30 bg-amber-500/5 text-amber-900 dark:text-amber-200",
        "flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs",
        className,
      )}
    >
      <CircleDashed
        aria-hidden="true"
        className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-300"
      />
      <div className="flex-1 leading-relaxed">
        {body ?? (
          <>
            <span className="font-medium">Forms editing is paused.</span> Your account manager has
            temporarily disabled editing form fields for your institution. Your forms still render
            for admissions and enrolment — you just can&apos;t change their fields right now.
            Contact your account manager to re-enable editing.
          </>
        )}
      </div>
    </div>
  );
}
